import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, Menu, X, User, Leaf, Heart, Shield, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F3F6F4]">
      {/* Ambient background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[#B2D3C2]/20 rounded-full blur-[120px] animate-drift"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#EAF4EC]/60 rounded-full blur-[100px] animate-drift-slow"></div>
        <div className="absolute top-1/2 left-1/3 w-[400px] h-[400px] bg-[#B2D3C2]/10 rounded-full blur-[80px] animate-drift-reverse"></div>
      </div>

      {/* Header */}
      <header className="relative z-50 border-b border-[#CEDACF]/50 bg-[#F3F6F4]/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Logo */}
            <div 
              className="text-xl sm:text-2xl font-semibold text-[#2F5F4F] cursor-pointer tracking-tight"
              style={{fontFamily: 'DM Serif Display, serif'}}
              onClick={() => navigate('/')}
            >
              OHMNAI
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6 lg:gap-8">
              <a href="#how-it-works" className="text-sm text-[#2F5F4F]/70 hover:text-[#2F5F4F] transition-colors duration-500">
                How it works
              </a>
              <a href="/why" className="text-sm text-[#2F5F4F]/70 hover:text-[#2F5F4F] transition-colors duration-500">
                Trust
              </a>
              <a href="#circle" className="text-sm text-[#2F5F4F]/70 hover:text-[#2F5F4F] transition-colors duration-500">
                Circle
              </a>
              
              {isAuthenticated ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  className="border-[#CEDACF] text-[#2F5F4F] hover:bg-[#EAF4EC] transition-all duration-500"
                >
                  <User className="h-4 w-4 mr-2" />
                  {user?.name?.split(' ')[0] || 'Dashboard'}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => navigate('/auth/email')}
                  className="bg-[#2F5F4F] text-white hover:bg-[#2F5F4F]/90 transition-all duration-500"
                >
                  Sign in
                </Button>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-[#2F5F4F]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden absolute top-full left-0 right-0 bg-[#F3F6F4]/95 backdrop-blur-lg border-b border-[#CEDACF]/50 transition-all duration-500 ${mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <div className="px-4 py-6 space-y-4">
            <a href="#how-it-works" className="block text-[#2F5F4F]/70 hover:text-[#2F5F4F] py-2 transition-colors duration-500" onClick={() => setMobileMenuOpen(false)}>
              How it works
            </a>
            <a href="/why" className="block text-[#2F5F4F]/70 hover:text-[#2F5F4F] py-2 transition-colors duration-500" onClick={() => setMobileMenuOpen(false)}>
              Trust & Ethics
            </a>
            <a href="#circle" className="block text-[#2F5F4F]/70 hover:text-[#2F5F4F] py-2 transition-colors duration-500" onClick={() => setMobileMenuOpen(false)}>
              Circle
            </a>
            <div className="pt-4 border-t border-[#CEDACF]/50 space-y-3">
              {isAuthenticated ? (
                <Button
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#2F5F4F] text-white"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <Button
                  onClick={() => { navigate('/auth/email'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#2F5F4F] text-white"
                >
                  Sign in
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-32 pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto text-center">
          {/* Tagline */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#EAF4EC] rounded-full text-sm text-[#2F5F4F] mb-8 animate-fade-in">
            <Leaf className="h-4 w-4" />
            <span>Values-first alignment</span>
          </div>

          {/* Main Headline */}
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-[#2F5F4F] mb-6 leading-[1.1] tracking-tight animate-fade-in-up"
            style={{fontFamily: 'DM Serif Display, serif', animationDelay: '0.1s'}}
          >
            Turn your values
            <br />
            <span className="text-[#2F5F4F]/70">into quiet action</span>
          </h1>

          {/* Subheadline */}
          <p 
            className="text-lg sm:text-xl text-[#2F5F4F]/60 mb-12 max-w-2xl mx-auto leading-relaxed animate-fade-in-up"
            style={{animationDelay: '0.2s'}}
          >
            OHMNAI helps you allocate time, money, and attention toward what matters—without pressure, without noise.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
            <Button
              onClick={() => navigate('/values-profile')}
              className="bg-[#2F5F4F] hover:bg-[#2F5F4F]/90 text-white px-8 py-6 text-base rounded-2xl transition-all duration-500 hover:shadow-xl hover:shadow-[#2F5F4F]/10"
            >
              Begin your profile
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="border-[#CEDACF] text-[#2F5F4F] hover:bg-[#EAF4EC] px-8 py-6 text-base rounded-2xl transition-all duration-500"
            >
              Learn more
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm text-[#2F5F4F]/50 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#B2D3C2]" />
              <span>No feeds or gamification</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#B2D3C2]" />
              <span>One suggestion at a time</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#B2D3C2]" />
              <span>Skip anytime. Always.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Cards */}
      <section className="relative z-10 px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Values Card */}
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-[#CEDACF]/30 shadow-sm hover:shadow-md transition-all duration-700 animate-fade-in-up" style={{animationDelay: '0.5s'}}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#EAF4EC] rounded-xl flex items-center justify-center">
                  <Heart className="h-5 w-5 text-[#2F5F4F]" />
                </div>
                <h3 className="text-sm font-medium text-[#2F5F4F]/60 uppercase tracking-wider">Your values</h3>
              </div>
              <div className="space-y-3">
                {['Climate Action', 'Education Access', 'Local Community'].map((value, i) => (
                  <div key={i} className="flex items-center gap-3 text-[#2F5F4F]">
                    <div className="w-2 h-2 bg-[#B2D3C2] rounded-full"></div>
                    <span className="text-sm sm:text-base">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Impact Card */}
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-[#CEDACF]/30 shadow-sm hover:shadow-md transition-all duration-700 animate-fade-in-up" style={{animationDelay: '0.6s'}}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#EAF4EC] rounded-xl flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-[#2F5F4F]" />
                </div>
                <h3 className="text-sm font-medium text-[#2F5F4F]/60 uppercase tracking-wider">Quiet impact</h3>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Micro-grant sent', value: '$5' },
                  { label: 'Time donated', value: '15 min' },
                  { label: 'Causes supported', value: '2' }
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="text-sm text-[#2F5F4F]/60">{item.label}</span>
                    <span className="text-sm sm:text-base font-medium text-[#2F5F4F]">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 bg-[#EAF4EC]/40 py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl text-[#2F5F4F] mb-4"
              style={{fontFamily: 'DM Serif Display, serif'}}
            >
              How it works
            </h2>
            <p className="text-[#2F5F4F]/60 max-w-xl mx-auto">
              Three simple steps. No complexity. No pressure.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                step: '01',
                title: 'Share your values',
                description: 'Tell us what matters to you. Not abstract ideals—what actually feels personal.'
              },
              {
                step: '02',
                title: 'Set your capacity',
                description: 'Time, money, attention. We respect what you realistically have right now.'
              },
              {
                step: '03',
                title: 'Receive gentle nudges',
                description: 'One suggestion at a time. Skip anytime. Silence is always valid.'
              }
            ].map((item, i) => (
              <div 
                key={i} 
                className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-[#CEDACF]/30 transition-all duration-700 hover:shadow-lg hover:bg-white/90"
              >
                <div className="text-4xl sm:text-5xl font-light text-[#B2D3C2] mb-4" style={{fontFamily: 'DM Serif Display, serif'}}>
                  {item.step}
                </div>
                <h3 className="text-lg sm:text-xl font-medium text-[#2F5F4F] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base text-[#2F5F4F]/60 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl text-[#2F5F4F] mb-6"
            style={{fontFamily: 'DM Serif Display, serif'}}
          >
            Built on trust
          </h2>
          <p className="text-lg text-[#2F5F4F]/60 mb-16 max-w-2xl mx-auto">
            OHMNAI is designed to align with you, not optimize you.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: Shield, title: 'Your data stays yours', desc: 'Export or delete anytime' },
              { icon: Heart, title: 'Non-persuasive', desc: 'No dark patterns ever' },
              { icon: Leaf, title: 'Gentle by design', desc: 'Slow, intentional UX' },
              { icon: Sparkles, title: 'Transparent AI', desc: 'See why we suggest' }
            ].map((item, i) => (
              <div key={i} className="bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#CEDACF]/30 transition-all duration-500 hover:bg-white/70">
                <item.icon className="h-6 w-6 text-[#2F5F4F] mb-4 mx-auto" />
                <h3 className="text-[#2F5F4F] font-medium mb-1">{item.title}</h3>
                <p className="text-sm text-[#2F5F4F]/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Circle Section */}
      <section id="circle" className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#2F5F4F] rounded-[2rem] sm:rounded-[3rem] p-8 sm:p-12 lg:p-16 text-white relative overflow-hidden">
            {/* Ambient decoration */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-[#B2D3C2]/10 rounded-full blur-[80px]"></div>
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-white/5 rounded-full blur-[60px]"></div>
            
            <div className="relative z-10">
              <div className="text-[#B2D3C2] text-sm font-medium mb-4 tracking-wider uppercase">
                Stewardship, not premium
              </div>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl mb-4"
                style={{fontFamily: 'DM Serif Display, serif'}}
              >
                OHMNAI Circle
              </h2>
              <p className="text-lg sm:text-xl text-white/70 mb-8 max-w-xl leading-relaxed">
                Members keep the system gentle, aligned, and growing. This is stewardship.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-10">
                {[
                  'Memory of past actions',
                  'Access to pooled coordination',
                  'Member input on direction',
                  'Early access to experiments'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 text-white/80">
                    <Check className="h-5 w-5 text-[#B2D3C2] flex-shrink-0" />
                    <span className="text-sm sm:text-base">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <Button
                  onClick={() => navigate('/circle')}
                  className="bg-white text-[#2F5F4F] hover:bg-[#EAF4EC] px-8 py-6 text-base rounded-2xl transition-all duration-500 shadow-lg hover:shadow-xl"
                >
                  Join the Circle
                </Button>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-light">$9</span>
                  <span className="text-white/60">/month</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-20 sm:py-28 px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 
            className="text-3xl sm:text-4xl text-[#2F5F4F] mb-6"
            style={{fontFamily: 'DM Serif Display, serif'}}
          >
            Ready to begin?
          </h2>
          <p className="text-[#2F5F4F]/60 mb-8">
            Start with your values. Everything flows from there.
          </p>
          <Button
            onClick={() => navigate('/values-profile')}
            className="bg-[#2F5F4F] hover:bg-[#2F5F4F]/90 text-white px-10 py-6 text-base rounded-2xl transition-all duration-500 hover:shadow-xl hover:shadow-[#2F5F4F]/10"
          >
            Create your profile
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#CEDACF]/30 bg-[#EAF4EC]/30 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <div 
              className="text-xl font-semibold text-[#2F5F4F]"
              style={{fontFamily: 'DM Serif Display, serif'}}
            >
              OHMNAI
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-[#2F5F4F]/60">
              <a href="/why" className="hover:text-[#2F5F4F] transition-colors duration-500">About</a>
              <a href="/vision" className="hover:text-[#2F5F4F] transition-colors duration-500">Vision</a>
              <a href="#" className="hover:text-[#2F5F4F] transition-colors duration-500">Privacy</a>
              <a href="#" className="hover:text-[#2F5F4F] transition-colors duration-500">Terms</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-[#CEDACF]/30 text-center">
            <p className="text-sm text-[#2F5F4F]/40">
              © 2025 OHMNAI. Who you are is what you choose to enable.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
